/**
 * A single headline number for a trader's process.
 *
 * ## Why bother, given how crude this is
 *
 * Because it works. A trader checks one number daily, notices it move, and asks
 * why — which is the entire point of a journal and the thing a page of
 * statistics never manages to provoke. The competing product ships one and it is
 * among the most-used things they have.
 *
 * ## Where this one differs, and why that matters more than the arithmetic
 *
 * Theirs is a black box: 0–100 across "profitability, risk management,
 * consistency and discipline", with no published weights and no way to see
 * which part moved. AEGIS cannot ship that, for the same reason `docs/08`
 * forbids an unexplained model reading — **a number a user cannot interrogate
 * is a claim, and this product does not make claims it will not show working
 * for.**
 *
 * So every component here reports its own inputs, its own value, and the one
 * sentence explaining what moved it. The composite is the sum; the parts are
 * the product.
 *
 * ## What it is not
 *
 * Not predictive, and the copy must never imply otherwise. It scores what has
 * already happened. A high score after twelve trades means twelve trades went
 * well, and `sampleWarning` says so rather than letting the number imply
 * confidence the data has not earned.
 *
 * Pure functions only.
 */
import { Decimal } from 'decimal.js';
import { dec, ZERO } from '../money.js';
import type { Trade } from './types.js';
import { dailyPnl, NO_BAND, summarise, type BreakevenOptions } from './analytics.js';

export type ComponentId = 'profitability' | 'risk' | 'consistency' | 'discipline';

export interface ScoreComponent {
  readonly id: ComponentId;
  readonly label: string;
  /** 0..25. Each component carries equal weight; see `WEIGHT`. */
  readonly points: Decimal;
  /** 0..1, before weighting. Kept so the UI can draw a meter without dividing. */
  readonly ratio: Decimal;
  /**
   * What actually produced the number, in the trader's units.
   *
   * The difference between a score and a verdict. Rendered under the component
   * so a trader can disagree with it specifically rather than in general.
   */
  readonly inputs: readonly { readonly label: string; readonly value: string }[];
  /** One sentence. Descriptive, never advisory — no "you should". */
  readonly reading: string;
}

export interface Score {
  /** 0..100, the sum of the four components. */
  readonly total: Decimal;
  readonly components: readonly ScoreComponent[];
  readonly tradeCount: number;
  /**
   * Set when the sample is too small for the number to mean much.
   *
   * Present as a sentence rather than suppressing the score: a hidden number
   * gets computed by hand and trusted more, not less.
   */
  readonly sampleWarning: string | null;
}

/** Each component is worth this many points. Equal by choice — see below. */
export const WEIGHT = new Decimal(25);

/**
 * Below this, the score is labelled provisional.
 *
 * Twenty closed trades is not a statistically defensible sample for anything.
 * It is the point at which a daily number stops swinging wildly on one trade,
 * which is the property that makes it usable as a habit.
 */
export const MIN_MEANINGFUL_TRADES = 20;

const clamp01 = (d: Decimal): Decimal =>
  d.lessThan(0) ? ZERO : d.greaterThan(1) ? new Decimal(1) : d;

const pct = (d: Decimal): string => `${d.times(100).toFixed(1)}%`;

export interface ScoreInputs {
  readonly trades: readonly Trade[];
  /**
   * Rule breaches in the period — a daily loss limit hit, a drawdown breached.
   * From the rule engine, not inferred from P&L.
   */
  readonly ruleBreaches: number;
  /** How many trading days the period covers, for breach and journalling rates. */
  readonly tradingDays: number;
  /** Trade ids the trader wrote a note on. Drives the discipline component. */
  readonly journalledTradeIds: ReadonlySet<string>;
  /** Trade ids assigned to a named strategy. */
  readonly strategyAssignedTradeIds: ReadonlySet<string>;
  /**
   * How much of the daily routine was kept over the period, 0..1.
   *
   * From `consistency()` in `daily.ts`. This is the join that makes the routine
   * matter: the competing product's checklist changes nothing except the
   * checklist, and here ticking it moves the number the trader already checks
   * every morning.
   *
   * Optional so a caller with no routine data scores the other two signals
   * rather than being penalised for a feature it did not ask about.
   */
  readonly routineAdherence?: Decimal;
  /**
   * The trader's breakeven band, if they set one.
   *
   * The score is where this matters most: profitability is scored on
   * expectancy, and a run of commission-sized scratches counted as losses drags
   * a number the trader is asked to trust.
   */
  readonly breakeven?: BreakevenOptions;
}

/**
 * Profitability — expectancy against risk taken, not profit.
 *
 * Scored on R rather than currency on purpose. A trader who made $10,000 by
 * risking $9,000 a trade is not outperforming one who made $2,000 risking $200,
 * and a score denominated in money says otherwise. Falls back to profit factor
 * when no trade carries a risk basis, and says which it used.
 */
function profitability(
  trades: readonly Trade[], be: BreakevenOptions = NO_BAND,
): ScoreComponent {
  const closed = trades.filter((t) => !t.isOpen);
  const s = summarise(closed, be);
  const withR = closed.filter((t) => t.rMultiple !== null);

  let ratio: Decimal;
  let basis: string;
  let headline: string;

  if (withR.length > 0) {
    const avgR = withR.reduce((a, t) => a.plus(t.rMultiple!), ZERO).div(withR.length);
    // 0R is a coin flip and scores half; +1R average is exceptional and caps it.
    ratio = clamp01(avgR.plus(1).div(2));
    basis = 'average R-multiple';
    headline = `${avgR.toFixed(2)}R`;
  } else if (s.profitFactor) {
    // 1.0 breaks even and scores half; 2.0 and above caps it.
    ratio = clamp01(s.profitFactor.minus(1));
    basis = 'profit factor (no risk basis recorded)';
    headline = s.profitFactor.toFixed(2);
  } else {
    ratio = s.netPnl.greaterThan(0) ? new Decimal(1) : ZERO;
    basis = 'net result (no losses to compare against)';
    headline = s.netPnl.toFixed(2);
  }

  return {
    id: 'profitability',
    label: 'Profitability',
    points: ratio.times(WEIGHT),
    ratio,
    inputs: [
      { label: 'Measured on', value: basis },
      { label: 'Value', value: headline },
      { label: 'Win rate', value: pct(s.winRate) },
      { label: 'Expectancy', value: s.expectancy.toFixed(2) },
    ],
    reading: withR.length > 0
      ? `Averaging ${headline} per trade across ${withR.length} trades with a recorded risk.`
      : `Scored on profit factor because no trade in this period recorded what was risked.`,
  };
}

/**
 * Risk — whether the trader stayed inside the limits they set.
 *
 * The one component that is not about outcome. A day that made money after
 * breaching a drawdown limit is a worse day than a flat one that did not, and
 * a score that cannot say so is measuring luck.
 */
function risk(inputs: ScoreInputs): ScoreComponent {
  const { ruleBreaches, tradingDays } = inputs;
  const closedTrades = inputs.trades.filter((t) => !t.isOpen);

  // A trader who has not traded has not breached anything, and awarding full
  // marks for that would mean the highest risk score in the product belongs to
  // an empty account. No activity, no points — the score measures how somebody
  // traded, not that they refrained.
  if (closedTrades.length === 0) {
    return {
      id: 'risk', label: 'Risk control', points: ZERO, ratio: ZERO,
      inputs: [{ label: 'Closed trades', value: '0' }],
      reading: 'No closed trades yet, so there is nothing to judge risk control on.',
    };
  }

  const days = Math.max(1, tradingDays);
  const breachRate = dec(ruleBreaches).div(days);
  // Any breach costs heavily: one in ten days halves this half of the score.
  const withinLimits = clamp01(new Decimal(1).minus(breachRate.times(5)));

  const withRisk = closedTrades.filter((t) => t.riskBasis !== null).length;
  const coverage = dec(withRisk).div(closedTrades.length);

  // Both halves, and the second one is not optional.
  //
  // Staying inside a limit is only half of risk control; the other half is
  // knowing what you are risking on each trade. Scoring on breaches alone gave
  // full marks to an account that recorded a stop on nothing — which was
  // visible on screen as "Risk control 25/25" directly above "Trades with a
  // recorded risk: 0.0%", a contradiction the trader would rightly not trust.
  //
  // Weighted toward breaches, because an actual breach is a worse fact about a
  // session than a missing field.
  const ratio = withinLimits.times('0.7').plus(coverage.times('0.3'));

  return {
    id: 'risk',
    label: 'Risk control',
    points: ratio.times(WEIGHT),
    ratio,
    inputs: [
      { label: 'Rule breaches', value: String(ruleBreaches) },
      { label: 'Trading days', value: String(tradingDays) },
      { label: 'Trades with a recorded risk', value: pct(coverage) },
    ],
    reading: [
      ruleBreaches === 0
        ? `No rule breach in ${tradingDays} trading ${tradingDays === 1 ? 'day' : 'days'}.`
        : `${ruleBreaches} breach${ruleBreaches === 1 ? '' : 'es'} across ${tradingDays} trading days.`,
      coverage.isZero()
        ? 'No trade records what was risked, so half of this component cannot be earned.'
        : `${pct(coverage)} of trades record what was risked.`,
    ].join(' '),
  };
}

/**
 * Consistency — how tightly daily results cluster.
 *
 * Coefficient of variation on daily net, so it is scale-free: a $200/day trader
 * and a $20,000/day trader are held to the same standard. A trader whose month
 * is one enormous day and nineteen small losses has a profitable month and no
 * process, and this is the component that says so.
 */
function consistency(inputs: ScoreInputs): ScoreComponent {
  // `dailyPnl` buckets by CME trading day rather than calendar day, which is
  // the difference between a Sunday-evening session belonging to Monday and
  // showing up as its own lonely outlier.
  const values = [...dailyPnl(inputs.trades).values()];

  if (values.length < 2) {
    return {
      id: 'consistency',
      label: 'Consistency',
      points: ZERO, ratio: ZERO,
      inputs: [{ label: 'Trading days', value: String(values.length) }],
      reading: 'Not enough days yet to say anything about consistency.',
    };
  }

  const mean = values.reduce((a, v) => a.plus(v), ZERO).div(values.length);
  const variance = values
    .reduce((a, v) => a.plus(v.minus(mean).pow(2)), ZERO)
    .div(values.length);
  const sd = variance.sqrt();
  const green = values.filter((v) => v.greaterThan(0)).length;

  // Scale-free dispersion. A standard deviation equal to the mean scores zero;
  // a quarter of it scores full marks. Uses |mean| so a losing period is
  // measured on the same axis rather than dividing by a negative.
  const absMean = mean.abs();
  const cv = absMean.isZero() ? new Decimal(4) : sd.div(absMean);
  const ratio = clamp01(new Decimal(1).minus(cv.minus(new Decimal(0.25)).div(new Decimal(0.75))));

  return {
    id: 'consistency',
    label: 'Consistency',
    points: ratio.times(WEIGHT),
    ratio,
    inputs: [
      { label: 'Trading days', value: String(values.length) },
      { label: 'Green days', value: `${green} of ${values.length}` },
      { label: 'Average day', value: mean.toFixed(2) },
      { label: 'Day-to-day spread', value: sd.toFixed(2) },
    ],
    reading: `Daily results vary by ${sd.toFixed(0)} against an average day of ${mean.toFixed(0)}.`,
  };
}

/**
 * Discipline — whether the trader did the work around the trade.
 *
 * Three signals: the trade was journalled, it was attached to a named strategy,
 * and the daily routine was kept. All three are things only the trader can do,
 * all three are the behaviours the product exists to encourage, and none is a
 * proxy for a result — which is what keeps this from being profitability
 * counted twice.
 *
 * The routine term is what closes the loop with `daily.ts`. A checklist that
 * moves nothing is a to-do list inside a journal; this one moves the number.
 */
function discipline(inputs: ScoreInputs): ScoreComponent {
  const closed = inputs.trades.filter((t) => !t.isOpen);
  if (closed.length === 0) {
    return {
      id: 'discipline', label: 'Discipline', points: ZERO, ratio: ZERO,
      inputs: [], reading: 'No closed trades in this period.',
    };
  }
  const journalled = closed.filter((t) => inputs.journalledTradeIds.has(t.id)).length;
  const assigned = closed.filter((t) => inputs.strategyAssignedTradeIds.has(t.id)).length;
  const jr = dec(journalled).div(closed.length);
  const ar = dec(assigned).div(closed.length);

  // Averaged over however many signals the caller supplied, so a deployment
  // without the daily loop is not silently marked down for its absence.
  const terms = [jr, ar];
  if (inputs.routineAdherence) terms.push(clamp01(inputs.routineAdherence));
  const ratio = clamp01(terms.reduce((a, t) => a.plus(t), ZERO).div(terms.length));

  const rows = [
    { label: 'Trades journalled', value: `${journalled} of ${closed.length}` },
    { label: 'Trades assigned to a strategy', value: `${assigned} of ${closed.length}` },
  ];
  if (inputs.routineAdherence) {
    rows.push({ label: 'Daily routine kept', value: pct(clamp01(inputs.routineAdherence)) });
  }

  return {
    id: 'discipline',
    label: 'Discipline',
    points: ratio.times(WEIGHT),
    ratio,
    inputs: rows,
    reading: [
      `${pct(jr)} of trades carry a note; ${pct(ar)} are attached to a named strategy.`,
      inputs.routineAdherence
        ? `The daily routine was kept on ${pct(clamp01(inputs.routineAdherence))} of judged days.`
        : '',
    ].filter(Boolean).join(' '),
  };
}

/** The composite. Four equally weighted components, each showing its working. */
export function computeScore(inputs: ScoreInputs): Score {
  const components = [
    profitability(inputs.trades, inputs.breakeven ?? NO_BAND),
    risk(inputs),
    consistency(inputs),
    discipline(inputs),
  ];
  const total = components.reduce((a, c) => a.plus(c.points), ZERO);
  const tradeCount = inputs.trades.filter((t) => !t.isOpen).length;

  return {
    total,
    components,
    tradeCount,
    sampleWarning: tradeCount < MIN_MEANINGFUL_TRADES
      ? `Based on ${tradeCount} closed ${tradeCount === 1 ? 'trade' : 'trades'}. `
        + `Below about ${MIN_MEANINGFUL_TRADES} this moves a long way on a single result.`
      : null,
  };
}
